import { useCallback, useMemo, useState } from 'react';
import { useLazyGetChatSessionsSupervisorHistoriesQuery } from 'services';
import { FindAllChatSessionsHistoryDto } from 'types';
import { DEFAULT_PAGE_SIZE } from 'utils';
const pageSize = DEFAULT_PAGE_SIZE;
export function useLazyOmniChatSessionSupervisorHistories() {
  const [trigger, { data: dataResponse, isLoading, isFetching, originalArgs }] =
    useLazyGetChatSessionsSupervisorHistoriesQuery();

  const [loading, setLoading] = useState(false);
  const { rows, count } = dataResponse?.data || {};
  const hasMore = Boolean(rows && count && rows.length < count);

  // Group rows by date
  const groupedData = useMemo(() => {
    if (!rows) return [];

    const grouped = rows.reduce(
      (acc, row) => {
        const dateKey = row.createdAt;
        if (!dateKey) return acc;

        const date = new Date(dateKey).toISOString().split('T')[0];

        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(row);

        return acc;
      },
      {} as Record<string, typeof rows>
    );

    // Convert object to array and sort by date (newest first)
    return Object.entries(grouped)
      .map(([date, sessions]) => ({
        date,
        sessions
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [rows, count]);

  const handleLoadMore = useCallback(() => {
    if (hasMore)
      trigger({
        ...originalArgs,
        limit: pageSize,
        skip: rows?.length
      });
  }, [hasMore, originalArgs, rows]);

  const fetchData = useCallback((value?: Partial<FindAllChatSessionsHistoryDto>) => {
    setLoading(true);
    trigger({
      skip: 0,
      limit: pageSize,
      ...originalArgs,
      ...value
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  return {
    data: rows || [],
    groupedData,
    count: count || 0,
    isLoading: isLoading || loading,
    hasMore,
    fetchData,
    handleLoadMore,
    isFetching: isFetching,
    originalArgs
  };
}
